import React, { useCallback, useEffect, useState } from 'react';
import { Box, Theme, useMediaQuery } from '@mui/material';
import { MuiButton } from '@ui/base_library';
import { makeStyles } from 'tss-react/mui';
import { useRouter } from 'next/router';
import palette from '@ui/palette';
import useStudioAvatarModal from '@avatar/hooks/useStudioAvatarModal';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { ReplacementsDictionaryProvider } from '@replacements/context/ReplacementsDictionaryContext';
import useEvaluateStudioProjectVersionFeatures from 'components/Studio/useEvaluateStudioProjectVersionFeatures';
import Tooltip from 'components/Tooltip';
import { Can } from '../../../context/projectAccess/ProjectAccessContext';
import {
  ProjectAccessAction,
  ProjectAccessSubject,
} from '../../../../server/abilities/ProjectAccessEnums';
import ActiveActorToggle from '../../../components/avatar/components/ActiveActorToggle';
import ResizableBox, { OnResizeData } from '../../../components/ResizeableBox';
import {
  RESPONSIVE_EDITOR_MAX_HEIGHT,
  RESPONSIVE_EDITOR_MIN_HEIGHT,
} from '../../../theme';
import Editor from '../../../components/Editor/Editor';
import StudioClipUserList, {
  StudioClipUserListProps,
} from '../../../components/StudioClipUserList';
import StudioClipPreviewListWrapper from '../../../components/StudioClipPreviewListWrapper';
import {
  STORAGE_KEY_EDITOR_INITIAL_HEIGHT,
  StudioPageProps,
} from './[projectId]';
import {
  ActorAccessLevel,
  ProjectAccessRoleEnum,
  StudioProjectVersion,
} from '../../../../server/graphql/__generated.types';
import useEditMode from '../../../components/Studio/ToolbarDrawer/useEditMode';
import storage from '../../../util/storage';
import {
  EventName,
  useEventsContext,
} from '../../../context/events/EventsContext';
import { usePromptsContext } from '../../../context/prompts/PromptsContext';
import {
  findFirstMatchingActorVariant,
  getFirstActorWithANonArchivedVariant,
  getFirstNonArchivedVariant,
} from '../../../components/avatar/utils/actor';
import useActiveActor from '../../../components/avatar/hooks/useActiveActor';
import useClipCreator from '../../../context/clips/useClipCreator';
import {
  CLIP_PREVIEWS,
  useStudioClipPreviewListContext,
} from '../../../context/previewClip/StudioPreviewClipContext';
import StudioUtilityBar from '../../../components/Studio/Navbar/StudioUtilityBar';
import useIsLegacyProjectViewOnly from '../../../components/Studio/legacy/useIsLegacyProjectViewOnly';
import Modal, { ModalBodyPaper } from '../../../components/Modal';
import { useUserContext } from '../../../context/user/UserContext';
import { EditMode } from '../../../store/EditorSlice';
import NavigationConfirmationForm from '../../../components/StudioClipPreviewListWrapper/NavigationConfirmation/NavigationConfirmationForm';
import {
  LayoutElementState,
  useLayoutContext,
} from '../../../components/BaseLayout/context/LayoutContext';
import { ClipObject } from '../../../context/clips';

interface ProjectPageContentProps {
  activeActor: ReturnType<typeof useActiveActor>['activeActor'];
  activeActorVariantVersion: ReturnType<
    typeof useActiveActor
  >['activeActorVariantVersion'];
  activeClipActor: ReturnType<typeof useActiveActor>['activeClipActor'];
  edit: StudioPageProps['edit'];
  editorInitialHeight?: number | null;
  project: StudioPageProps['project'];
  projectScopedActors: StudioPageProps['projectScopedActors'];
  setActiveActor: ReturnType<typeof useActiveActor>['setActiveActor'];
  setActiveActorVariant: ReturnType<
    typeof useActiveActor
  >['setActiveActorVariant'];
}

const useStyles = makeStyles()(theme => ({
  editor: {
    display: 'grid',
    gridTemplateRows: `minmax(50%, 100%) auto ${theme.spacing(9)}`,
    [theme.breakpoints.up('sm')]: {
      gridTemplateRows: `minmax(50%, 100%) auto ${theme.spacing(7.5)}`,
    },
  },
  disabledImport: {
    cursor: 'not-allowed !important',
    color: `${palette.gray_400} !important`,
  },
  importScriptButton: {
    color: palette.gray_50,
    textTransform: 'none',
    padding: theme.spacing(1.5, 2),
    '&:hover': {
      backgroundColor: palette.gray_700,
    },
    flexShrink: 0,
  },
}));

const ProjectPageContent = ({
  activeActor,
  activeActorVariantVersion,
  activeClipActor,
  edit,
  editorInitialHeight,
  project,
  projectScopedActors,
  setActiveActor,
  setActiveActorVariant,
}: ProjectPageContentProps) => {
  const { previews, resetPreviews } = useStudioClipPreviewListContext();
  const { data: userQuery } = useUserContext();
  const [, dispatchPrompt] = usePromptsContext();
  const [
    isNavigationConfirmationModalOpen,
    setIsNavigationConfirmationModalOpen,
  ] = useState(false);
  const [shouldIgnoreRouteChangeEvent, setShouldIgnoreRouteChangeEvent] =
    useState(false);

  const { importScriptDisabled } = useEvaluateStudioProjectVersionFeatures({
    studioVersion: project.studioVersion,
  });

  // Todo: update this when new project studio versions are introduced
  const canUpdateAvatarVariantVersion =
    project.studioVersion === StudioProjectVersion.Legacy;
  const canAccessCuesMode = project.studioVersion === StudioProjectVersion.V2;
  const { isScriptMode, isCuesMode, editMode, onEditModeChange } = useEditMode({
    initialEdit: edit,
    canAccessCuesMode,
  });
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [eventTracking] = useEventsContext();
  const router = useRouter();
  const [, dispatch] = usePromptsContext();
  const { classes, cx } = useStyles();
  const isDesktop = useMediaQuery<Theme>(theme => theme.breakpoints.up('md'));
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { setRightRailVisibility } = useLayoutContext();
  const handleDrawerOpen = useCallback(() => {
    setRightRailVisibility(LayoutElementState.OPEN);
  }, [setRightRailVisibility]);

  const handleRouteChangeStart = useCallback(
    (url: string) => {
      const savedPreviews =
        previews && previews?.size > 0
          ? Array.from(previews).filter(
              ([_clipId, clip], _index) => clip.isSaved
            )
          : [];

      const watchRouteChange =
        !shouldIgnoreRouteChangeEvent &&
        !userQuery?.user.settings.doNotShowNavigationConfirmationModal &&
        editMode === EditMode.CUES &&
        previews &&
        previews?.size > 0 &&
        savedPreviews.length < previews.size;

      // If route change url is different from current url
      if (router?.asPath !== url && watchRouteChange) {
        setNextUrl(url);
        setIsNavigationConfirmationModalOpen(true);

        // Emit error to stop the routing (this error is silenced in ErrorBoundary component)
        router?.events.emit(
          'routeChangeError',
          {
            message: 'Route Cancelled',
          },
          url
        );

        // Expecting an error object to be thrown, but want to silently fail here
        // eslint-disable-next-line no-throw-literal
        throw 'Route Cancelled';
      } else if (!watchRouteChange) {
        if (previews && previews.size > 0) {
          eventTracking.track({
            type: EventName.ClipPreviewSessionCount,
            data: {
              total: previews.size,
              saved: savedPreviews.length,
            },
          });
        }
        // If we allow navigation, we want to clear previews in storage
        storage.removeItem(CLIP_PREVIEWS);
      }
    },
    [
      editMode,
      eventTracking,
      previews,
      router?.asPath,
      router?.events,
      shouldIgnoreRouteChangeEvent,
      userQuery?.user.settings.doNotShowNavigationConfirmationModal,
    ]
  );

  useEffect(() => {
    if (editMode === EditMode.CUES) {
      router?.events.on('routeChangeStart', handleRouteChangeStart);
      return () => {
        router?.events.off('routeChangeStart', handleRouteChangeStart);
      };
    }
  }, [
    shouldIgnoreRouteChangeEvent,
    userQuery?.user.settings.doNotShowNavigationConfirmationModal,
    editMode,
    previews,
    router?.events,
    handleRouteChangeStart,
  ]);

  const handleReplaceEditorValue = useCallback(() => {
    window.scrollTo(0, 0);
  }, []);

  const [
    ,
    { loading: isTTSRequestLoading, error: previewClipError, currentClipId },
    cancelPreviewFn,
    previewFn,
    previewClip,
    resetPreviewClip,
  ] = useClipCreator(project.id, activeClipActor);

  const { addClipToPreviews, deleteClipFromPreviews } =
    useStudioClipPreviewListContext();

  const { openAvatarModal } = useStudioAvatarModal({
    setActiveAvatar: setActiveActor,
  });

  const handleRetakeSameActor = useCallback<
    StudioClipUserListProps['setActiveActor']
  >(
    (newActor, _newVariant, newActorVariantTTSVersion) => {
      let newActiveActor;
      let newActiveVariant;

      newActiveActor = projectScopedActors.find(
        actor => actor.id === newActor.id
      );

      if (newActiveActor) {
        // Find an active variant with the same Id and version
        newActiveVariant = findFirstMatchingActorVariant({
          baseVariant: newActor.variant,
          variants: newActiveActor.variants,
          matchVariantId: true,
          matchVersion: true,
          includeArchived: false,
        });

        // If the exact match was not found, try to find an active variant with the same Id and different version
        if (!newActiveVariant) {
          newActiveVariant = findFirstMatchingActorVariant({
            baseVariant: newActor.variant,
            variants: newActiveActor.variants,
            matchVariantId: true,
            matchVersion: false,
            includeArchived: false,
          });
        }

        // If still not found, try to find any variant for the actor that is not archived
        if (!newActiveVariant) {
          newActiveVariant = getFirstNonArchivedVariant(
            newActiveActor.variants
          );
        }
      }

      // If the actor or the variant was not found, pick the first actor with an active variant
      if (!newActiveActor || !newActiveVariant) {
        newActiveActor =
          getFirstActorWithANonArchivedVariant(projectScopedActors)!;

        newActiveVariant = newActiveActor.variants.find(
          variant => !variant.isArchived
        );
      }

      setActiveActor(
        newActiveActor,
        newActiveVariant,
        newActorVariantTTSVersion
      );
    },
    [setActiveActor, projectScopedActors]
  );

  const handleResize = useCallback(
    ({ height, keyUp, mouseUp, touchEnd }: OnResizeData) => {
      storage.setItem(
        storage.getScopedKey(STORAGE_KEY_EDITOR_INITIAL_HEIGHT),
        height.toString()
      );
      if (keyUp || mouseUp || touchEnd) {
        eventTracking.trackEditorResized({ projectId: project.id, height });
      }
    },
    [eventTracking, project]
  );

  // Responsible for closing modal on cancel
  const handleNavigationConfirmationSuccess = () => {
    if (router) {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      setIsNavigationConfirmationModalOpen(false);
    }
  };

  // Responsible for handling submit of form
  const handleNavigationConfirmationOnConfirm = () => {
    // if theres a nextUrl (user clicked on nav bar vs script tab)
    if (nextUrl) {
      router?.push(nextUrl);
      storage.removeItem(CLIP_PREVIEWS);
    } else {
      // user is switching tabs
      setShouldIgnoreRouteChangeEvent(true);
      handleNavigationConfirmationSuccess();
      onEditModeChange(EditMode.SCRIPT);
      setShouldIgnoreRouteChangeEvent(false);
    }

    resetPreviews();
  };

  /**
   * Used to render navigation warning modal if there are unsaved previews
   */

  const previewsExist = previews && previews?.size > 0;
  const savedPreviews = previewsExist
    ? Array.from(previews).filter(([_clipId, clip], _index) => clip.isSaved)
    : [];

  const disableScriptTabChange =
    previewsExist &&
    savedPreviews.length < previews.size &&
    !userQuery?.user.settings.doNotShowNavigationConfirmationModal;

  const handlePreviewDelete: (
    clipInput: ClipObject
  ) => Promise<void> = clipInput => {
    deleteClipFromPreviews(clipInput);
    return Promise.resolve();
  };
  const handleImportScript = () => {
    dispatchPrompt({
      type: 'OpenImportScriptPrompt',
      payload: {
        onSubmit: (scripts: string | undefined) => {},
      },
    });
  };
  const isReadOnlyAccess =
    project.projectAccess.viewerAccessRole?.id ===
    ProjectAccessRoleEnum.ReadOnly;
  const isLegacyViewOnly = useIsLegacyProjectViewOnly(project.studioVersion);
  const legacyOrReadOnly = isLegacyViewOnly || isReadOnlyAccess;
  return (
    <ReplacementsDictionaryProvider>
      <>
        {canAccessCuesMode && !legacyOrReadOnly && (
          <StudioUtilityBar
            onTabChange={onEditModeChange}
            disableScriptTabChange={disableScriptTabChange}
            setIsNavigationConfirmationModalOpen={(isOpen: boolean) =>
              setIsNavigationConfirmationModalOpen(isOpen)
            }
          />
        )}
        {!legacyOrReadOnly && (
          <Box mt={2} mb={3}>
            <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <ActiveActorToggle
                    isScriptMode={isScriptMode}
                    actor={activeActor.actor}
                    activeVariant={activeActor.variant}
                    activeVariantVersion={activeActorVariantVersion}
                    onActorAvatarClick={handleDrawerOpen}
                    onVariantClick={setActiveActorVariant}
                    // Only enabled when the <StudioToolbarDrawer /> is a temporary drawer (offcanvas)
                    actorAvatarClickDisabled={isDesktop}
                    setActiveActor={setActiveActor}
                    projectVersion={project.studioVersion}
                    canUpdateAvatarVariantVersion={
                      canUpdateAvatarVariantVersion
                    }
                  />
                </Box>
                {!isCuesMode && (
                  <Tooltip
                    text={
                      importScriptDisabled
                        ? 'Import script is not available for this language.'
                        : ''
                    }
                    disabled={
                      importScriptDisabled === false || legacyOrReadOnly
                    }
                    placement="top-start"
                  >
                    <MuiButton
                      data-e2e="studio-import-script-open-cta"
                      size="small"
                      color="inherit"
                      className={cx(
                        classes.importScriptButton,
                        importScriptDisabled && classes.disabledImport
                      )}
                      startIcon={<Icon type="import" />}
                      onClick={() => {
                        if (importScriptDisabled || legacyOrReadOnly) {
                          return;
                        }
                        handleImportScript();
                      }}
                    >
                      Import script
                    </MuiButton>
                  </Tooltip>
                )}
              </Box>
            </Can>
          </Box>
        )}
        {/* Editor */}
        {!legacyOrReadOnly && (
          <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
            <Box marginY={3}>
              <ResizableBox
                className={classes.editor}
                constraints={{
                  maxHeight: RESPONSIVE_EDITOR_MAX_HEIGHT,
                  minHeight: RESPONSIVE_EDITOR_MIN_HEIGHT,
                }}
                initialHeight={editorInitialHeight}
                onResize={handleResize}
              >
                <Editor
                  project={project}
                  clipActor={activeClipActor}
                  isLocked={
                    activeActor.actor.accessLevel === ActorAccessLevel.Locked
                  }
                  isEditable={isScriptMode && !legacyOrReadOnly}
                  canAccessCuesMode={canAccessCuesMode}
                  isCuesMode={isCuesMode}
                  previewFn={previewFn}
                  isTTSRequestLoading={isTTSRequestLoading}
                  cancelPreviewFn={cancelPreviewFn}
                  previewClipError={previewClipError}
                  downloadError={downloadError}
                  setDownloadError={setDownloadError}
                />
              </ResizableBox>
            </Box>
          </Can>
        )}
        {/* Clips */}
        <Box marginTop={4} pb={25} position="relative">
          {isCuesMode && (
            <Box
              display="flex"
              flexDirection="row"
              marginTop="8px"
              marginBottom="24px"
              gap={1}
            >
              <Typography variant="h5" bold uppercased>
                Previews
              </Typography>
              <Typography variant="pSm">
                Save previews to your project to use them as clips
              </Typography>
            </Box>
          )}
          <StudioClipUserList
            visible={isScriptMode}
            project={project}
            onReplaceEditorValue={handleReplaceEditorValue}
            setActiveActor={handleRetakeSameActor}
            onPromptDisplay={() => {
              openAvatarModal({
                event: {
                  type: EventName.ActorSelectionPromptOpened,
                  data: { path: router?.asPath || '', programmatic: true },
                },
                payload: {
                  activeAvatarId: activeActor?.actor?.id,
                  activeVariantId: activeActor?.variant?.id,
                  studioVersion: project.studioVersion,
                },
              });
            }}
            setDownloadError={setDownloadError}
          />
          {isCuesMode && (
            <Box>
              <StudioClipPreviewListWrapper
                actor={activeClipActor}
                addClipToPreviews={addClipToPreviews}
                currentClipId={currentClipId}
                deleteClipFromPreviews={handlePreviewDelete}
                previewClip={previewClip}
                previewClipError={previewClipError}
                resetPreviewClip={resetPreviewClip}
                project={project}
              />
            </Box>
          )}
        </Box>
        <Modal
          open={isNavigationConfirmationModalOpen}
          onClose={() => setIsNavigationConfirmationModalOpen(false)}
          includePaper={false}
        >
          <ModalBodyPaper
            onClose={() => setIsNavigationConfirmationModalOpen(false)}
            includeDismissIcon
            includePadding={false}
            includeMaxWidth={416}
          >
            <div
              id="modal-title"
              aria-label="Confirm navigation with unsaved changes prompt"
            />
            <div
              id="modal-description"
              aria-label="This prompt displays a form that confirms unsaved changes before navigating away from the page"
            />
            <NavigationConfirmationForm
              onSuccess={handleNavigationConfirmationSuccess}
              onConfirm={handleNavigationConfirmationOnConfirm}
            />
          </ModalBodyPaper>
        </Modal>
      </>
    </ReplacementsDictionaryProvider>
  );
};

export default ProjectPageContent;
